import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import apiUrl from "../utility/apiUrl";
import Carousel from "./ui/Carousel";
import { SkeletonCollegeCard } from "./ui/Skeleton";
import { fetchListDeduped, readCachedList } from "../utility/homeCollegeCache";
import HomeCollegeCarouselCard from "./HomeCollegeCarouselCard";
import {
  MoreHorizontal,
} from "lucide-react";

const TopCollege = () => {
  const [collegeData, setCollegeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const limit = 12;

  const dedupeColleges = (list) => {
    const seen = new Set();
    const safe = Array.isArray(list) ? list : [];
    const out = [];
    for (const c of safe) {
      const idKey = c?.id !== undefined && c?.id !== null ? `id:${c.id}` : null;
      const fallbackKey = `name:${String(c?.collegeName || "").trim().toLowerCase()}|city:${String(
        c?.city || ""
      )
        .trim()
        .toLowerCase()}`;
      const key = idKey || fallbackKey;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  };

  useEffect(() => {
    const cached = readCachedList("top");
    if (cached) {
      setCollegeData(cached);
      setLoading(false);
    }

    const authToken = Cookies.get("token");
    const headers = {
      "Content-Type": "application/json",
      "ngrok-skip-browser-warning": "any",
    };
    if (authToken) headers.Authorization = String(authToken);

    fetchListDeduped(
      "top",
      apiUrl + "/api/v1/college/top/list?limit=30",
      { headers, method: "POST" }
    ).then((list) => {
      setCollegeData(Array.isArray(list) ? list : []);
      setLoading(false);
    });
  }, []);

  const tabs = useMemo(
    () => [
      { key: "all", label: "All" },
      { key: "engineering", label: "Engineering" },
      { key: "medical", label: "Medical" },
      { key: "mba", label: "MBA" },
      { key: "more", label: "…", icon: MoreHorizontal },
    ],
    []
  );

  const items = useMemo(() => {
    const safe = dedupeColleges(collegeData);
    const maxItems = Math.max(10, Math.min(15, limit));

    const normalize = (v) =>
      String(v || "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();

    // Category membership (no keyword matching against college name).
    // Uses the structured fields: college.collegeCategory and courses[].category.
    const toCategoryKey = (raw) => {
      const v = normalize(raw);
      if (!v) return null;

      // Engineering
      if (v === "engineering") return "engineering";
      if (v.includes("engineering")) return "engineering";
      if (
        v === "b.tech" ||
        v === "b tech" ||
        v === "btech" ||
        v === "b.e" ||
        v === "b e" ||
        v === "be" ||
        v === "m.tech" ||
        v === "m tech" ||
        v === "mtech"
      )
        return "engineering";

      // Medical
      if (v === "medical") return "medical";
      if (
        v.includes("mbbs") ||
        v.includes("bds") ||
        v.includes("bams") ||
        v.includes("bhms") ||
        v.includes("dental") ||
        v.includes("nursing") ||
        v.includes("pharm") ||
        v.includes("pharmacy")
      )
        return "medical";

      // MBA / Management
      if (v === "mba" || v === "management") return "mba";
      if (v.includes("mba") || v.includes("pgdm") || v.includes("management")) return "mba";
      if (v.includes("business administration") || v === "bba" || v === "bbm") return "mba";

      return null;
    };

    const getCollegeCategoryKeys = (college) => {
      const keys = new Set();
      const direct = toCategoryKey(college?.collegeCategory);
      if (direct) keys.add(direct);

      const courses = Array.isArray(college?.courses) ? college.courses : [];
      for (const c of courses) {
        const k = toCategoryKey(c?.category);
        if (k) keys.add(k);
      }

      return keys;
    };

    const matchCategory = (college, categoryKey) => {
      if (!college) return false;
      if (categoryKey === "all") return true;
      const keys = getCollegeCategoryKeys(college);
      return keys.has(categoryKey);
    };

    const filtered = safe.filter((c) => matchCategory(c, activeTab));
    const base = activeTab === "all" ? safe : filtered;
    return base.slice(0, maxItems);
  }, [collegeData, activeTab]);

  const canLoop = items.length > 2;


  return (
    <div>
      {/* Tabs row (Premium Colleges) */}
      <div className="mb-4">
        <div className="text-sm font-semibold text-slate-600">Premium Colleges</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {tabs.map((t) => {
            const active = activeTab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                className={
                  "inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold border transition " +
                  (active
                    ? "bg-brand-600 border-brand-600 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50")
                }
                onClick={() => {
                  if (t.key === "more") return;
                  setActiveTab(t.key);
                }}
                aria-pressed={active}
                title={t.key === "more" ? "More" : t.label}
              >
                {Icon ? <Icon className="h-4 w-4" /> : t.label}
                {!Icon ? null : <span className="sr-only">{t.label}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SkeletonCollegeCard />
          <SkeletonCollegeCard />
        </div>
      ) : (
      <Carousel key={activeTab} itemClassName="w-full md:w-[calc(50%-8px)]" autoplay={canLoop}>
        {items.map((college, index) => (
          <HomeCollegeCarouselCard key={college?.id || index} college={college} />
        ))}
      </Carousel>
      )}
    </div>
  );
};

export default TopCollege;
